/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { EditorView, lineNumbers } from '@codemirror/view';
import FeelEditorBase from '@bpmn-io/feel-editor';

import { EditorWrapper } from './FeelEditor.styled';

const EMPTY_FN = () => {};

/**
 * FEEL editor component
 *
 * @param {FeelEditorProps} props
 * @returns {JSX.Element}
 */
/**
 * @typedef {Object} FeelEditorProps
 * @property {string} [initialValue] - Initial editor value
 * @property {(value: string) => void} [onInput] - Called when input changes
 * @property {(diagnostics: Array) => void} [onLint] - Called with lint results
 * @property {string} [placeholder] - Placeholder text
 * @property {HTMLElement} [tooltipContainer] - Container for tooltips
 * @property {string} expressionFromFeelCopilot - expression suggested by the FEEL Copilot, not typed by the user
 */

/** @type {import('react').ForwardRefRenderFunction<any, FeelEditorProps>} */
const FeelEditorWithRef = (props, ref) => {
  const {
    initialValue = '',
    onInput = EMPTY_FN,
    onLint = EMPTY_FN,
    placeholder,
    tooltipContainer,
    expressionFromFeelCopilot = null
  } = props;

  const editorRef = useRef(null);
  const editorInstanceRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      const editorElement = editorRef.current?.querySelector('.cm-content');
      if (editorElement) {
        editorElement.focus();
      }
    },
    setValue: (value) => {
      editorInstanceRef.current?.setValue(value);
    }
  }));

  useEffect(() => {
    const container = editorRef.current;
    if (!container) {
      return;
    }

    const editorInstance = new FeelEditorBase({
      container,
      value: initialValue,
      onChange: onInput,
      onLint,
      placeholder,
      tooltipContainer,
      variables: [],
      extensions: [lineNumbers(), EditorView.lineWrapping]
    });

    editorInstanceRef.current = editorInstance;

    return () => {
      onLint([]);
      container.innerHTML = '';
    };
  }, [onInput, onLint, placeholder, tooltipContainer]);

  useEffect(() => {
    if (expressionFromFeelCopilot) {
      // When a value update comes from another component (e.g., FEEL Copilot)
      editorInstanceRef?.current?.setValue(expressionFromFeelCopilot);
    }
  }, [expressionFromFeelCopilot]);

  return (
    <EditorWrapper
      ref={editorRef}
      data-testid="feel-editor"
      role={'complementary'}
      aria-label={'FEEL expression editor'}
    />
  );
};

export const FeelEditor = forwardRef(FeelEditorWithRef);
